import React, { useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Search, ArrowUpDown, ChevronLeft, ChevronRight, Users } from "lucide-react";

// Custom dynamic tick to handle rendering and native hover tooltips for long names
const DynamicYAxisTick = ({ x, y, payload }) => {
  const label = payload.value;
  const maxCharacterLimit = 22; // Upper bound threshold before applying ellipse truncation
  const displayLabel = label.length > maxCharacterLimit 
    ? `${label.substring(0, maxCharacterLimit - 3)}...` 
    : label;

  return (
    <text x={x - 6} y={y + 4} textAnchor="end" fill="#64748b" fontSize={10}>
      <title>{label}</title> {/* Native browser tooltip on axis label hover */}
      {displayLabel}
    </text>
  );
};

export default function CustomerLifetimeValue({ data = [] }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "TotalSpent", direction: "desc" });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // 1. Handle Sorting & Searching
  const processedData = useMemo(() => {
    let filtered = data.filter((item) =>
      item.accdesc.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.account.includes(searchTerm)
    );

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === "asc" ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [data, searchTerm, sortConfig]);

  // 2. Extract Top 5 for Chart Representation
  const topCustomersData = useMemo(() => {
    return [...data]
      .sort((a, b) => b.TotalSpent - a.TotalSpent)
      .slice(0, 5)
      .map(item => ({
        name: item.accdesc.split("(")[0].trim(), // Keeps complete dynamic text
        Spent: item.TotalSpent,
        fullLabel: item.accdesc
      }));
  }, [data]);

  // 3. Dynamically compute Y-Axis box width based on actual data text lengths
  const dynamicYAxisWidth = useMemo(() => {
    if (topCustomersData.length === 0) return 90;
    // Find the longest name length among top 5, capped at character limits
    const maxChars = Math.max(...topCustomersData.map(d => Math.min(d.name.length, 22)));
    // ~6.5 pixels allocated per text character + 15px safe padding margin
    return Math.max(90, maxChars * 6.5 + 15);
  }, [topCustomersData]);

  // 4. Handle Pagination
  const totalPages = Math.ceil(processedData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return processedData.slice(start, start + itemsPerPage);
  }, [processedData, currentPage]);

  const requestSort = (key) => {
    let direction = "desc";
    if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc";
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-2 my-2">
      
      {/* Visual Component: Top 5 Bars */}
      <div style={{maxHeight:"445px"}} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col">
        <div>
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <span className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg"><Users size={16}/></span>
            Top 5 Customers by Revenue
          </h3>
          <p className="text-[11px] text-slate-400 mt-0.5">Highest value spending accounts</p>
        </div>
        
        <div className="h-56 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topCustomersData} layout="vertical" margin={{ left: 5, right: 20, top: 5, bottom: 5 }}>
              <XAxis type="number" hide />
              <YAxis 
                dataKey="name" 
                type="category" 
                axisLine={false} 
                tickLine={false} 
                width={dynamicYAxisWidth} // Dynamically adjusts spacing based on names
                tick={<DynamicYAxisTick />} // Gracefully cuts text with an ellipsis if super long
              />
              {/* Tooltip text visibility fixed by supplying itemStyle and labelStyle */}
              <Tooltip 
                formatter={(value) => [`AED ${Number(value).toLocaleString()}`, 'Total Spent']}
                contentStyle={{ background: '#0f172a', borderRadius: '8px', border: 'none' }}
                itemStyle={{ color: '#ffffff', fontSize: '11px' }}
                labelStyle={{ color: '#94a3b8', fontSize: '11px', fontWeight: '600' }}
              />
              <Bar dataKey="Spent" radius={[0, 6, 6, 0]} barSize={14}>
                {topCustomersData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={index === 0 ? "#6366f1" : index === 1 ? "#818cf8" : "#a5b4fc"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Structured Interactive Table Element */}
      <div className="xl:col-span-2 bg-white p-5 mb-14 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
        <div>
          {/* Header & Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-800">Customer Performance Directory</h3>
              <p className="text-[11px] text-slate-400 mt-0.5">Showing {processedData.length} records matching parameters</p>
            </div>
            
            {/* Search Input field */}
            <div className="relative max-w-xs w-full">
              <Search className="absolute left-3 top-2.5 h-3.5 width-3.5 text-slate-400" size={14} />
              <input
                type="text"
                placeholder="Search by ID or name..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-9 pr-4 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Interactive Scroll Window table */}
          <div className="overflow-auto max-h-[300px]">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100 text-[11px] font-semibold text-slate-500 bg-slate-50/50">
                  <th className="px-3 py-2">Account ID / Customer</th>
                  <th className="px-3 py-2 text-right cursor-pointer hover:bg-slate-100 rounded-md" onClick={() => requestSort("TotalOrders")}>
                    <div className="flex items-center justify-end gap-1">Total Orders <ArrowUpDown size={10} /></div>
                  </th>
                  <th className="px-3 py-2 text-right cursor-pointer hover:bg-slate-100 rounded-md" onClick={() => requestSort("AvgOrderValue")}>
                    <div className="flex items-center justify-end gap-1">Avg Order Value <ArrowUpDown size={10} /></div>
                  </th>
                  <th className="px-3 py-2 text-right cursor-pointer hover:bg-slate-100 rounded-md" onClick={() => requestSort("TotalSpent")}>
                    <div className="flex items-center justify-end gap-1">Total Spent <ArrowUpDown size={10} /></div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 text-xs">
                {paginatedData.length > 0 ? (
                  paginatedData.map((row) => (
                    <tr key={row.account} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-3 py-2 max-w-[200px] truncate">
                        <span className="block text-[10px] font-mono text-slate-400">{row.account}</span>
                        <span className="font-medium text-slate-700 block truncate" title={row.accdesc}>{row.accdesc}</span>
                      </td>
                      <td className="px-3 py-2 text-right text-slate-600 font-medium">{row.TotalOrders}</td>
                      <td className="px-3 py-2 text-right text-slate-600">AED {row.AvgOrderValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                      <td className="px-3 py-2 text-right text-indigo-600 font-semibold">AED {row.TotalSpent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-12 text-slate-400 text-xs">No matching customer entries located</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Dynamic Pagination Actions */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-slate-100 pt-3 mt-2 text-xs text-slate-500">
            <span>Page {currentPage} of {totalPages}</span>
            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
                className="p-1 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
                className="p-1 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}